import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import api from '../../../lib/api';
import { RetestTag, fmtPx, fmtTime, setupLabel } from './RetestTag';
import { StructureBlock, ConfirmationBlock, TradePlanBlock, ScoreBlock, HistoryTimeline } from './RetestSections';
import { SignalBanner, IndicatorsBlock, MomentumBlock, CrossVenueBlock } from './RetestSignalBlocks';

const DATA_LABEL = {
  DATA_STALE: 'DATA STALE', INSUFFICIENT_HISTORY: 'INSUFFICIENT HISTORY', FUTURE_DATED: 'FUTURE-DATED DATA',
  INCOMPLETE_TIMEFRAME: 'INCOMPLETE TIMEFRAME', INVALID_DATA: 'INVALID DATA', NO_DATA: 'NO DATA',
};

function TfTabs({ data, tf, setTf, symbol }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={`retest-tf-tabs-${symbol}`}>
      {data.supported_tfs.map((t) => {
        const r = data.timeframes[t];
        const best = r.setups?.[0];
        const sl = setupLabel(best);
        const label = r.data_status !== 'OK' ? DATA_LABEL[r.data_status] || r.data_status : sl.text;
        return (
          <button key={t} onClick={() => setTf(t)} data-testid={`retest-tf-tab-${symbol}-${t}`}
                  className={`px-2 py-1 border text-left min-w-[92px] transition-colors duration-150 ${tf === t ? 'border-cyan-sys bg-cyan-sys/10' : 'border-[var(--border-default)] hover:border-cyan-sys/50'}`}>
            <div className="text-[10px] tracking-widest uppercase text-cyan-sys">{t}</div>
            <div className={`text-[8px] tracking-wider uppercase ${r.data_status !== 'OK' ? 'text-sell' : sl.cls}`}>
              {label}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Context({ data, row, r, labels }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]" data-testid="retest-market-context">
      <span className="text-cyan-sys">{data.base}/{data.quote}</span>
      <span className="tabular-nums" data-testid="retest-current-price">${fmtPx(data.price)}</span>
      <span className="text-[var(--txt-muted)]" data-testid="retest-venue">{data.venue_label} · {fmtTime(data.fetched_at)} · candle age {r?.data_age_s ?? '—'}s</span>
      <span className="text-[var(--txt-muted)]">HTF: {(r?.htf_trends || []).map((h) => `${h.tf} ${h.trend}`).join(' · ') || 'n/a (highest TF)'}</span>
      <div className="flex flex-wrap gap-1">
        {(row?.matched || []).map((m) => (
          <span key={m} className="text-[8px] tracking-widest uppercase px-1 py-0.5 border border-cyan-sys/60 text-cyan-sys">{labels?.[m] || m.replace(/_/g, ' ')}</span>
        ))}
      </div>
    </div>
  );
}

export default function RetestDetail({ row, initialTf, venue = 'binance', labels }) {
  const symbol = row.symbol;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tf, setTf] = useState(initialTf || '1h');
  const [idx, setIdx] = useState(0);

  const load = useCallback(async (auto = false) => {
    setBusy(true); setErr(null);
    try {
      const { data: d } = await api.get(`/crypto/retest/${symbol}`, { params: { venue } });
      setData(d);
      // one automatic retry when a provider hiccup left some TFs empty
      const missing = Object.values(d.timeframes || {}).some((x) => x.data_status === 'NO_DATA');
      if (missing && !auto) setTimeout(() => load(true), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'failed to load retest analysis');
    } finally { setBusy(false); }
  }, [symbol, venue]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // open on the setup behind the scanner row's tag when it is on this TF
    const key = (row.retest?.tags || []).find((t) => t.tf === tf)?.setup_key;
    const i = key ? (data?.timeframes?.[tf]?.setups || []).findIndex((x) => x.setup_key === key) : -1;
    setIdx(i > 0 ? i : 0);
  }, [tf, data, row]);

  if (!data && busy) {
    return <div className="p-4 flex items-center gap-2 text-[10px] text-[var(--txt-muted)]" data-testid={`retest-loading-${symbol}`}><Loader2 size={12} className="animate-spin" />analysing 5m · 15m · 1h · 4h · 1d…</div>;
  }
  if (err && !data) {
    return <div className="p-4 text-[10px] text-sell" data-testid={`retest-error-${symbol}`}>// {err} <button onClick={load} className="underline ml-2">retry</button></div>;
  }
  if (!data) return null;
  const r = data.timeframes[tf];
  const setups = r?.setups || [];
  const s = setups[idx];

  return (
    <div className="p-3 space-y-3 bg-[var(--bg-panel)] border-l-2 border-cyan-sys/60 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={`retest-detail-${symbol}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <Context data={data} row={row} r={r} labels={labels} />
        <button onClick={load} disabled={busy} data-testid={`retest-refresh-${symbol}`}
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-[var(--border-default)] text-[9px] tracking-widest uppercase text-[var(--txt-secondary)] hover:text-cyan-sys hover:border-cyan-sys disabled:opacity-40">
          {busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} refresh
        </button>
      </div>
      <TfTabs data={data} tf={tf} setTf={setTf} symbol={symbol} />
      <div className="flex items-center gap-1.5 text-[9px] text-amber-300/80" data-testid="retest-analysis-only-note">
        <ShieldAlert size={11} /> analysis only — a confirmed retest is not an order, and no trading gates are changed
      </div>
      {r.data_status !== 'OK' && (
        <div className="text-[10px] text-sell" data-testid="retest-data-status">{DATA_LABEL[r.data_status] || r.data_status} — {r.data_reason}</div>
      )}
      {r.data_status === 'OK' && s && <SignalBanner s={s} />}
      {r.data_status === 'OK' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <IndicatorsBlock ind={r.indicators} tvUrl={data.tradingview?.[tf]} venueLabel={data.venue_label} />
          {s && <MomentumBlock s={s} own={r.momentum} ltf={r.ltf_momentum} />}
          {s && <CrossVenueBlock s={s} />}
        </div>
      )}
      {r.data_status === 'OK' && setups.length === 0 && (
        <div className="text-[10px] text-[var(--txt-muted)]" data-testid="retest-empty">// no retest setup on {tf} — no recent break/bounce with a valid pullback</div>
      )}
      {setups.length > 1 && (
        <div className="flex flex-wrap gap-1" data-testid="retest-setup-switcher">
          {setups.map((x, i) => (
            <button key={x.setup_key} onClick={() => setIdx(i)} data-testid={`retest-setup-btn-${i}`}
                    className={`px-2 py-0.5 border text-[9px] tracking-widest uppercase ${i === idx ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)]'}`}>
              {x.kind.replace(/_RETEST/, '').replace('_', ' ')} level · {setupLabel(x).text}
            </button>
          ))}
        </div>
      )}
      {s && (
        <>
          <div className="flex flex-wrap gap-1">{s.tags.map((t, i) => <RetestTag key={t.label} tag={t} testId={`retest-detail-tag-${i}`} />)}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
            <StructureBlock s={s} />
            <ConfirmationBlock s={s} />
            <TradePlanBlock s={s} />
            <ScoreBlock s={s} />
            <HistoryTimeline s={s} />
          </div>
        </>
      )}
    </div>
  );
}
